import React, { useEffect, useState } from 'react';
import { useTheme } from 'styled-components';

import DisplayLogo from '@savgroup-front-common/core/atoms/DisplayLogo/DisplayLogo';
import { getCustomComponentProperty } from '@savgroup-front-common/core/helpers';
import { LanguageChooser } from '@savgroup-front-common/core/molecules/LanguageChooser';
import { useGetSellerAcceptedLanguages } from '@savgroup-front-common/core/molecules/LanguageChooser/hooks/useGetSellerAcceptedLanguages';
import useLangageChooser from '@savgroup-front-common/core/molecules/LanguageChooser/hooks/useLangageChooser';
import {
  LOGO_POSITION,
  SUPPORTED_COMPONENTS,
  SUPPORTED_PROPERTIES,
} from '@savgroup-front-common/types';

import { AuthConfiguration } from '../../../../configuration';

import {
  $EmptyDiv,
  $HeaderContainer,
  $MenuContainer,
  $RightSide,
} from './Header.styles';
import { useGetSellerUrl } from './hooks/useGetSellerUrl';
import { useHeaderActions } from './hooks/useHeaderActions';
import { LogoutButton } from './LogoutButton/LogoutButton';
import { ProfileMenu } from './ProfileMenu/ProfileMenu';

interface HeaderTestProps {
  sellerId: string;
  enableServicePortal?: boolean;
}

const Header: React.FC<HeaderTestProps> = ({
  sellerId,
  enableServicePortal,
}) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [rightSideWidth, setRightSideWidth] = useState(0);
  const { currentLanguage, handleChangeLanguage, isLoading } =
    useLangageChooser();

  const theme = useTheme();

  const position = getCustomComponentProperty({
    theme,
    componentName: SUPPORTED_COMPONENTS.LOGO,
    property: SUPPORTED_PROPERTIES.POSITION,
    fallback: LOGO_POSITION.LEFT,
  }) as LOGO_POSITION;

  const { acceptedLanguagesBySeller } = useGetSellerAcceptedLanguages({
    sellerId,
  });

  const isLanguageChooseDisplayed = !!acceptedLanguagesBySeller?.length;
  const hasMultipleLanguages = (acceptedLanguagesBySeller?.length ?? 0) > 1;

  const { isLogoutEnabled, logout } = useHeaderActions({ sellerId });

  useEffect(() => {
    if (ref.current) {
      setRightSideWidth(ref.current.offsetWidth);
    }
  }, [ref.current?.offsetWidth]);

  const { sellerUrl } = useGetSellerUrl({ sellerId });

  return (
    <$HeaderContainer
      componentThemeName={SUPPORTED_COMPONENTS.APP_HEADER}
      enableServicePortal={enableServicePortal}
    >
      <$MenuContainer $position={position}>
        {((position === LOGO_POSITION.CENTER && isLanguageChooseDisplayed) ||
          !isLanguageChooseDisplayed) && <$EmptyDiv $width={rightSideWidth} />}
        <DisplayLogo
          sellerId={sellerId}
          clientId={AuthConfiguration.clientId}
          href={sellerUrl.href}
          isExternal={sellerUrl.isExternal}
        />
        <$RightSide ref={ref}>
          {isLogoutEnabled && (
            <>
              {hasMultipleLanguages && (
                <ProfileMenu
                  acceptedLanguagesBySeller={acceptedLanguagesBySeller ?? []}
                  currentLanguage={currentLanguage}
                  onChangeLanguage={handleChangeLanguage}
                  onLogout={logout}
                />
              )}
              {!hasMultipleLanguages && <LogoutButton onLogout={logout} />}
            </>
          )}

          {!isLogoutEnabled && (
            <LanguageChooser
              acceptedLanguagesBySeller={acceptedLanguagesBySeller}
              currentLanguage={currentLanguage}
              onChangeLanguage={handleChangeLanguage}
              isLoading={isLoading}
            />
          )}
        </$RightSide>
      </$MenuContainer>
    </$HeaderContainer>
  );
};

export default Header;
